import {Component, Emit, Prop, Watch} from "vue-property-decorator"
import {Item} from "@/types/Component"
import Modal from "@/assets/common/Modal/Modal.vue"
import draggable from 'vuedraggable'
import {isItem, isItemList} from "@/types/guards"
import ApiClient from "@/assets/common/api-client/api-client"
import {ModalBase} from "@/types/ModalBase"
import { toCamel, toSnake } from 'snake-camel';
import store from '@/assets/common/store/store';

type ResponseItem = {
    id: string;
    name: string;
    is_hidden: boolean
}

type ValidateParam = {
    nameLength: number
}

@Component({
    components: {
        Modal,
        draggable
    }
})
export default class ItemSelectModal extends ModalBase {
    @Prop({required: false})
    private initialValue!: Item[] | Item | null

    @Prop({required: true})
    apiPath!: string

    @Prop({default: () => []})
    options!: string[]

    @Prop({required: true})
    itemKindName!: string

    @Prop({default: () => true})
    filterable!: boolean
    filter: string | null = null

    @Prop({default: () => true})
    editable!: boolean

    @Prop({default: () => false})
    sortable!: boolean

    @Prop({default: () => false})
    multiple!: boolean

    @Prop({default: () => false})
    required!: boolean

    selectedValue: Item | Item[] | null = []
    candidates: Item[] = []
    isLoading = false // TODO: Implement loader spinner
    mode: 'select' | 'edit' = 'select'
    validateParam: ValidateParam = { nameLength: 255 }
    candidateEditor = new ItemSelectModal.CandidateEditor([], this.$apiClient, "", this.$toasted, this.validateParam)

    drag = false
    addCandidateModalVisible = false

    @Watch("isModalVisible")
    onOpen(newValue: boolean) {
        this.isLoading = true
        if (!newValue) {
            return
        }
        this.filter = null

        this.fetchCandidates().then(items => {
            this.candidates = items
            this.candidateEditor = new ItemSelectModal.CandidateEditor(items, this.$apiClient, this.apiPath, this.$toasted, this.validateParam)
            this.selectedValue = this.multiple ? [] : null

            if (this.multiple && isItemList(this.initialValue)) {
                this.selectedValue = this.initialValue.map(item => {
                    const search = this.candidates.find(candidateItem => candidateItem.id === item.id)
                    // if (!search) {
                        // throw new Error(`Item [${item.id}] not found on master.`)
                    // }
                    return search
                }).filter(search => !!search) as Item[]
            } else if (!this.multiple && isItem(this.initialValue)) {
                const initialValue = this.initialValue as Item
                this.selectedValue = this.candidates.find(candidate => candidate.id === initialValue.id)!
            }
        })
        .finally(() => {
            this.isLoading = false
        })
    }

    disabledCheck(id: string): boolean {
        if(this.multiple && isItemList(this.initialValue)) {
            const item = this.initialValue.find(v => v.id === id)
            return item?.disabledCheck !== undefined && item.disabledCheck
        }
        else if(!this.multiple && isItem(this.initialValue)) {
            return this.initialValue.id === id && this.initialValue.disabledCheck !== undefined && this.initialValue.disabledCheck
        }
        return false
    }

    get filteredCandidates(): Item[] {
        if (this.filter === null) {
            return this.candidates
        }
        const filteredCandidate = this.candidates.filter(item => item.name.indexOf(this.filter!) >= 0)
        if (this.itemKindName =="作業項目" && this.filter.length >0){
            const selectedId = this.selectedValue as Item
            const isItemSelectedCheck = filteredCandidate.some(candidate => candidate.id === selectedId?.id)
            if(!isItemSelectedCheck){
                this.selectedValue  = null;
            }
      }
        return filteredCandidate;
    }

    get title(): string {
        switch (this.mode) {
            case 'select':
                return `${this.itemKindName}の選択`
            case 'edit':
                return `${this.itemKindName}の編集`
        }
    }

    get filterPlaceholder(): string {
        return `${this.itemKindName}の検索`
    }

    async fetchCandidates(): Promise<Item[]> {
        const optionsPath = this.options.length === 0 ? '' : this.options.reduce((pre, cur) => `${pre}${cur}&`, '?').slice(0,-1)
        const response = (await this.$apiClient.doRequest('get', `${this.apiPath}${optionsPath}`))[this.apiPath.replace('/', '')]
        return response.map((res: ResponseItem) => toCamel(res) as Item)
    }

    selectItem(item: Item) {
        if (!this.multiple) {
            this.selectedValue = item
        } else {
            const selectedValue = this.selectedValue as Item[]
            const idx = selectedValue.indexOf(item)
            if (idx === -1) {
                selectedValue.push(item)
            } else {
                selectedValue.splice(idx, 1)
            }
        }
    }

    @Emit()
    close() {
        this.inputChanged()
        return this.selectedValue
    }

    @Emit()
    cancel() {
        this.mode = 'select'
        this.isModalVisible = false
        this.$store.dispatch('doUpdateIsInputChanged', false);
    }

    updateItem(item: Item) {
        const newName = this.candidateEditor.newName
        const updateItem = {
            id: item.id,
            name: newName,
            isHidden: item.isHidden
        }
        this.candidateEditor.update(updateItem)
        .then(() => {
            item.name = newName
            this.$emit('updateItem', item)
        }).catch(() => {
            return
        })
    }

    get isValid() {
        return true
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public static CandidateEditor = class CandidateEditor {
        private readonly apiClient: ApiClient
        private readonly apiPath: string
        private readonly candidates: Item[]
        private readonly toasted: any
        private value: Item | null = null
        addItemName = ''
        newName = ''
        validateParam: ValidateParam

        constructor(candidates: Item[], apiClient: ApiClient, apiPath: string, toasted: any, validateParam: ValidateParam) {
            this.apiClient = apiClient
            this.apiPath = apiPath
            this.candidates = candidates
            this.toasted = toasted
            this.validateParam = validateParam
        }

        set updatingTarget(value: Item | null) {
            this.value = value
        }

        get updatingTarget() {
            return this.value
        }

        public add() {
            if(!this.validate(this.addItemName)) {
                return false
            }
            const item = {
                name: this.addItemName
            }
            this.apiClient.doRequest('post', this.apiPath, item)
                .then(({id}) => {
                    this.candidates.push({
                        id,
                        name: item.name,
                        isHidden: false
                    } as Item)
                    this.showMessage('保存しました。')
                    this.addItemName = ''
                })
                .catch(() => {
                    this.showMessage('保存に失敗しました。', true)
                })
            }

        public edit(candidate: Item) {
            this.newName = candidate.name
            this.updatingTarget = candidate
        }

        public cancelEdit() {
            this.newName = ''
            this.updatingTarget = null
        }

        public update(candidate: Item): Promise<any> {
            if(!this.validate(this.newName)) {
                return Promise.reject('valid error')
            }
            const params = toSnake(candidate)
            return this.apiClient.doRequest('put', `${this.apiPath}/${this.updatingTarget!.id}`, params)
            .then(() => {
                this.showMessage('変更を保存しました。')
            })
            .catch((error) => {
                this.showMessage('保存に失敗しました。', true)
                return Promise.reject(error)
            }).finally(() => {
                this.updatingTarget = null
            })
        }

        public remove(candidate: Item) {
            if(!window.confirm('非表示にしますか？')){
                return false
            }
            candidate.isHidden = true
            const params = toSnake(candidate)
            this.apiClient.doRequest('put', `${this.apiPath}/${candidate.id}`, params)
            .then(() => {
                this.showMessage('非表示にしました。')
            })
            .catch(() => {
                candidate.isHidden = false
                this.showMessage('変更に失敗しました。', true)
            })
        }

        public reorder(candidate: Item, order: number) {
            // TODO: Implement this.
            throw new Error("Not implemented yet.")
        }

        private validate(name: string) {
            // 名前の長さ
            if(name.length > this.validateParam.nameLength){
                window.alert(`項目名は${this.validateParam.nameLength}文字以内で入力してください（現在は${name.length}文字）`)
                return false
            }
            return true
        }

        private showMessage(message: string, isError?: boolean) {
            if(isError) {
                this.toasted.error(message)
            } else {
                this.toasted.success(message)
            }
        }
    }

    @Emit('inputChanged')
    private inputChanged(){
        if(store.getters.isInputChanged==false){
            this.$store.dispatch('doUpdateIsInputChanged', true);
            }
    }
}

